import { AnimatedGroup } from '@tailark/core/ui/animated-group'
import { Button } from '@tailark/core/ui/button'
import { TextEffect } from '@tailark/core/ui/text-effect'
import Image from 'next/image'
import Link from 'next/link'

import { HeroProductScreenshot } from '../components/HeroProductScreenshot'
import NoiseBackground from '../components/NoiseBackground'
import type { CommonTranslations } from '../types/site-copy'

const transitionVariants = {
    item: {
        hidden: { opacity: 0, filter: 'blur(12px)', y: 12 },
        visible: { opacity: 1, filter: 'blur(0px)', y: 0, transition: { type: 'spring' as const, bounce: 0.3, duration: 1.5 } },
    },
}

interface HeroSectionProps {
    translations: CommonTranslations
    redirectUrl: string
}

export default function HeroSection({ translations, redirectUrl }: HeroSectionProps) {
    const t = translations

    return (
        <section className="overflow-hidden relative">
            <NoiseBackground className="-z-30" />
            <div className="relative pt-24 md:pt-36">
                <AnimatedGroup
                    variants={{
                        container: { visible: { transition: { delayChildren: 1 } } },
                        item: {
                            hidden: { opacity: 0, y: 20 },
                            visible: {
                                opacity: 1,
                                y: 0,
                                transition: { type: 'spring', bounce: 0.3, duration: 2 },
                            },
                        },
                    }}
                    className="absolute inset-0 -z-20">
                    <Image
                        src="/image.png"
                        alt="background"
                        className="absolute inset-x-0 top-56 -z-20 hidden lg:top-32 dark:block"
                        width="3276"
                        height="4095"
                    />
                </AnimatedGroup>
                <div className="absolute inset-0 -z-10 size-full [background:radial-gradient(125%_125%_at_50%_100%,transparent_0%,var(--color-background)_75%)]" />
                <div className="mx-auto max-w-7xl px-6">
                    <div className="text-center sm:mx-auto lg:mr-auto lg:mt-0">
                        <TextEffect preset="fade-in-blur" speedSegment={0.3} as="h1" className="mt-8 text-balance text-6xl md:text-5xl lg:mt-16 xl:text-[4rem]">
                            {t.home.title}
                        </TextEffect>
                        <TextEffect
                            per="line"
                            preset="fade-in-blur"
                            speedSegment={0.3}
                            delay={0.5}
                            as="p"
                            className="mx-auto mt-8 max-w-2xl text-balance text-md text-foreground/80">
                            {t.home.description}
                        </TextEffect>
                        <AnimatedGroup
                            variants={{
                                container: { visible: { transition: { staggerChildren: 0.05, delayChildren: 0.75 } } },
                                ...transitionVariants,
                            }}
                            className="mt-12 flex flex-col items-center justify-center gap-2 md:flex-row">
                            <div className="bg-foreground/10 rounded-[calc(var(--radius-xl)+0.125rem)] border p-0.5">
                                <Button asChild size="lg" className="px-5 text-base">
                                    <Link href={redirectUrl}>
                                        <span className="text-nowrap">{t.common.startBuilding}</span>
                                    </Link>
                                </Button>
                            </div>
                        </AnimatedGroup>
                    </div>
                </div>
                <AnimatedGroup
                    variants={{
                        container: { visible: { transition: { staggerChildren: 0.05, delayChildren: 0.75 } } },
                        ...transitionVariants,
                    }}>
                    <div className="relative -mr-56 mt-8 overflow-hidden px-2 sm:mr-0 sm:mt-12 md:mt-20">
                        <div aria-hidden className="bg-linear-to-b to-background absolute inset-0 z-10 from-transparent from-35%" />
                        <HeroProductScreenshot />
                    </div>
                </AnimatedGroup>
            </div>
        </section>
    )
}
